/**
 * @file: 层板商品从左到右排列布局
 * @author: xiaolong.huang@dmall.com
 */

import * as CubeTree from '../../../cubeTree/index';
import FrontCubeData from '../../../frontCubeData/index';
import Utils from '../../../utils/util';
import Move from '../../move/index';
import { layerLink } from '../link';
import { containSubGoodsSizeByCubeId } from '../../../UIToolkit/goods/index';
import { textareaSize } from '../../../UIToolkit/textarea/index';
import { cubeModify } from '../../../update/cubeModify';
import { layerCubesTotalWidth, layerIsGrouped } from '../../../UIToolkit/layer/layerInfo';
import { layerCubeSize } from '../../../UIToolkit/size/index';
import { getCubeInTargetWidth } from '../../../UIToolkit/query/index';

export default function leftToRight(layer: CubeId): void {
    // 先对层板正常排列一次
    layout(layer);

    // 触发cube挪动算法，溢出后移，空置前移
    moveCube(layer);
}

// 层板从左到右排列算法
function layout(layer: CubeId): void {
    let layoutCubeIds: Array<CubeId> = CubeTree.getChildCubeIds(layer);
    let startPos: number = startLeftCompute(layer);

    for (let i = 0; i < layoutCubeIds.length; i++) {
        let cubeId: CubeId = layoutCubeIds[i];
        let layoutCubeSize: PhysicsSize = layerCubeSize(cubeId);

        if (CubeTree.isBafflePlate(cubeId)) {
            startPos = FrontCubeData.getNumberValue(cubeId, 'posX');
        }

        startPos = Utils.toFixedNumber(startPos, 2);
        cubeModify(cubeId, {
            posWidth: 0,
            posX: startPos,
            posY: 0
        });
        startPos = Utils.math(startPos, layoutCubeSize.width, '+');
    }
}

/**
 * 1. 如果层板存在闲置空间，则需要从后面的层板挪动cube到当前层板，填充闲置空间
 * 2. 如果层板存在溢出cube，则需要把溢出的cube移动到后一个层板
 */
function moveCube(layerId: CubeId): void {
    let link: LinkedListInstance|null = layerLink(layerId);
    let emptyWidth: PhysicsMeasurement = layerEmptyWidth(layerId, link);

    if (emptyWidth >= 0) {
        // 前移
        fill(layerId, link, emptyWidth);

        // 前移完成后，再执行一次排序
        layout(layerId);
        return;
    }

    // 后移，不需要重新排序
    overflow(layerId, link);
}

// 前移
function fill(layerId: CubeId, link: LinkedListInstance, emptyWidth: PhysicsMeasurement): void {
    let behindLinkLayers: CubeId[] = link.behindOf(layerId);
    let behindCubes: CubeId[] = CubeTree.findParentsChild(behindLinkLayers);
    let fillCubes: CubeId[] = getCubeInTargetWidth(behindCubes, emptyWidth);
    let logicMoveTaskQueue: LogicMoveTask[] = makeFillTaskQueue(fillCubes, layerId);

    Move.logic(logicMoveTaskQueue);
}

// 溢出后移
function overflow(layerId: CubeId, link: LinkedListInstance): void {
    let overflowCubes: CubeId[] = findOverFlowCube(layerId, link);
    let next: LinkedNodeInstance|null = link.findNext(layerId);
    let nextLayer: CubeId|null = next ? next.val : null;

    if (!nextLayer) {
        return;
    }

    let logicMoveTaskQueue: LogicMoveTask[] = makeOverFlowTaskQueue(overflowCubes, nextLayer);

    overflowCubes.forEach((cubeId: CubeId): void => {
        let cube: CubeNode = CubeTree.findByCubeId(cubeId);

        cube.jView.addClass('is-hide');
    });

    Move.logic(logicMoveTaskQueue);
}

function findOverFlowCube(layerId: CubeId, link: LinkedListInstance): CubeId[] {
    let result: CubeId[] = [];
    let childIds: CubeId[] = CubeTree.getChildCubeIds(layerId);
    let moduleWidth: number = FrontCubeData.getNumberValue(layerId, 'moduleWidth');
    let extendLeft: number = FrontCubeData.getNumberValue(layerId, 'extendLeft');
    let extendRight: number = FrontCubeData.getNumberValue(layerId, 'extendRight');
    let totalWidth: PhysicsMeasurement = moduleWidth;

    if (link.isHead(layerId)) {
        totalWidth = Utils.math(extendLeft, totalWidth, '+');
    }

    if (link.isLast(layerId)) {
        totalWidth = Utils.math(extendRight, totalWidth, '+');
    }

    for (let i = childIds.length - 1; i >= 0; i--) {
        let left: PhysicsMeasurement = FrontCubeData.getNumberValue(childIds[i], 'posX');

        if (CubeTree.isBafflePlate(childIds[i])) {
            continue;
        }

        left >= totalWidth && result.push(childIds[i]);
    }

    return result;
}

function makeFillTaskQueue(cubeIds: CubeId[], layerId: CubeId): LogicMoveTask[] {
    let result: LogicMoveTask[] = [];

    cubeIds.forEach((cubeId: CubeId): void => {
        let oldParent: CubeId|null = CubeTree.findParentIdByCubeId(cubeId);

        result.push({
            from: {
                parent: oldParent,
                moveTarget: cubeId
            },
            to: {
                parent: layerId,
                insertBefore: null
            }
        });
    });

    return result;
}

function makeOverFlowTaskQueue(cubeIds: CubeId[], layerId: CubeId): LogicMoveTask[] {
    let result: LogicMoveTask[] = [];
    let insertTarget: CubeId|null = CubeTree.findTheFirstChildCubeId(layerId);

    cubeIds.forEach((cubeId: CubeId, index: number): void => {
        let oldParent: CubeId|null = CubeTree.findParentIdByCubeId(cubeId);

        result.push({
            from: {
                parent: oldParent,
                moveTarget: cubeId
            },
            to: {
                parent: layerId,
                insertBefore: insertTarget
            }
        });
        insertTarget = cubeId;
    });

    return result;
}

// 计算层板空置区域宽度
function layerEmptyWidth(layerId: CubeId, link: LinkedListInstance|null): PhysicsMeasurement {
    let result: PhysicsMeasurement = 0;
    let children: CubeId[] = CubeTree.findChildCubeIds(layerId);
    let usedWidth: PhysicsMeasurement = theLastCubeRightLine(layerId);
    let moduleWidth: number = FrontCubeData.getNumberValue(layerId, 'moduleWidth');
    let extendLeft: number = FrontCubeData.getNumberValue(layerId, 'extendLeft');
    let extendRight: number = FrontCubeData.getNumberValue(layerId, 'extendRight');
    let totalWidth: PhysicsMeasurement = moduleWidth;

    if (link.isHead(layerId)) {
        totalWidth = Utils.math(extendLeft, totalWidth, '+');
    }

    if (link.isLast(layerId)) {
        totalWidth = Utils.math(extendRight, totalWidth, '+');
    }

    if (link.length > 1 && !link.isHead(layerId)) {
        let prevLayer: CubeId = link.findPrev(layerId).val;
        let prevOverFlowWidth: PhysicsMeasurement = overFlowWidth(prevLayer);

        totalWidth = Utils.math(totalWidth, prevOverFlowWidth, '-');
    }

    result = Utils.math(totalWidth, usedWidth, '-');

    return result;
}

function theLastCubeRightLine(layer: CubeId): PhysicsMeasurement {
    let rightLine: PhysicsMeasurement = 0;
    let lastCube: CubeId|null = CubeTree.findTheLastChildCubeId(layer);

    if (lastCube) {
        let left: PhysicsMeasurement = FrontCubeData.getNumberValue(lastCube, 'posX');
        let width: PhysicsMeasurement = layerCubeSize(lastCube).width;

        rightLine = Utils.math(left, width, '+');
    }

    return rightLine;
}

/**
 * 右侧溢出宽度, 不用考虑右侧延伸量, 有可能是负数
 */
function overFlowWidth(layerId: CubeId): PhysicsMeasurement {
    let lastCube: CubeId|null = CubeTree.findTheLastChildCubeId(layerId);
    let moduleWidth: number = FrontCubeData.getNumberValue(layerId, 'moduleWidth');
    let rightLine: PhysicsMeasurement = 0;

    if (lastCube) {
        let left: PhysicsMeasurement = FrontCubeData.getNumberValue(lastCube, 'posX');
        let width: PhysicsMeasurement = layerCubeSize(lastCube).width;

        rightLine = Utils.math(left, width, '+');
    }


    return Utils.math(rightLine, moduleWidth, '-');
}

// 起始坐标计算
function startLeftCompute(layerId: CubeId): PhysicsMeasurement {
    let extendLeft: number = FrontCubeData.getNumberValue(layerId, 'extendLeft');
    let link: LinkedListInstance|null = layerLink(layerId);

    if ((link && link.isHead(layerId)) || !link) {
        return -extendLeft;
    }

    let prevLayer: CubeId = link.findPrev(layerId).val;
    let prevOverFlowWidth: PhysicsMeasurement = overFlowWidth(prevLayer);

    return prevOverFlowWidth;
}
